import { useState , useEffect} from "react";
import { motion } from "framer-motion";
import { Download, Sun, Moon, ArrowDown, ArrowUp } from "lucide-react";
import { Link , animateScroll as scroll} from "react-scroll";

function App() {
  const [darkMode, setDarkMode] = useState(true);
  const [showUpArrow, setShowUpArrow] = useState(false);

  const toggleTheme = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle("dark");
  };

  // Detect scroll position to toggle arrow direction
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setShowUpArrow(scrollY > window.innerHeight * 0.5); // halfway down Hero
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className={`${darkMode ? "dark" : ""}`}>
      <div className="min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 dark:from-gray-900 dark:via-gray-800 dark:to-black text-gray-900 dark:text-white flex flex-col items-center px-6 transition-colors duration-700">

        {/* Theme Toggle */}
        {/* Top Bar with Theme Toggle + Contact Links */}
        <div className="absolute top-5 right-5 flex items-center gap-4">
          {/* Contact Links */}
          <div className="flex items-center gap-4 text-gray-700 dark:text-gray-300">
            <a
              href="mailto:krishbhanushali@gmail.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title="Email"
            >
              <i className="ri-mail-line text-xl"></i>
            </a>
            <a
              href="https://www.linkedin.com/in/krishbhanushali"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title="LinkedIn"
            >
              <i className="ri-linkedin-box-line text-xl"></i>
            </a>
            <a
              href="https://github.com/krishbhanushali"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title="GitHub"
            >
              <i className="ri-github-line text-xl"></i>
            </a>
            <a
              href="https://medium.com/@krishbhanushali"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              title="Medium"
            >
              <i className="ri-medium-line text-xl"></i>
            </a>
          </div>

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:scale-110 transition-transform"
            title="Toggle Theme"
          >
            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>
        </div>


        {/* Hero Section */}
        <motion.section
          id="hero"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="flex flex-col items-center justify-center text-center min-h-screen"
        >
          {/* Logo */}
          <motion.img
            src="/kblogo4.png"
            alt="KB Logo"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="w-28 h-28 mb-6 drop-shadow-lg"
          />
          <h1 className="text-5xl font-bold mb-4">Krish Bhanushali</h1>
          <p className="text-lg text-gray-700 dark:text-gray-400 max-w-2xl">
            Software Engineer specializing in Distributed Systems, AWS Infrastructure, and Cloud Automation.
          </p>
          <a href="/Krish_Bhanushali_Resume.pdf" download>
            <button className="mt-6 px-6 py-3 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold flex items-center gap-2">
              <Download className="w-5 h-5" /> Download Resume
            </button>
          </a>
          {/* Scroll Arrow */}
          {!showUpArrow ? (
            <Link
              to="about"
              smooth={true}
              duration={800}
              offset={-50}
              className="absolute bottom-10 cursor-pointer"
            >
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
                className="flex flex-col items-center text-blue-600 dark:text-blue-400"
              >
                <ArrowDown className="w-8 h-8" />
                <span className="text-sm mt-1 text-gray-600 dark:text-gray-400">Scroll</span>
              </motion.div>
            </Link>
          ) : (
            <motion.div
              onClick={() => scroll.scrollToTop({ duration: 800 })}
              animate={{ y: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="absolute bottom-10 cursor-pointer flex flex-col items-center text-blue-600 dark:text-blue-400"
            >
              <ArrowUp className="w-8 h-8" />
              <span className="text-sm mt-1 text-gray-600 dark:text-gray-400">Top</span>
            </motion.div>
          )}
        </motion.section>

        {/* About Me */}
        <motion.section
          id="about"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-20 text-center"
        >
          <h2 className="text-3xl font-semibold mb-4">About Me</h2>
          <p className="text-gray-700 dark:text-gray-400 text-md leading-relaxed">
            Results-driven Software Development Engineer with 8+ years of experience building scalable distributed systems, high-throughput backend services, and AWS-based cloud platforms. Skilled in Java, Go, and Python, I specialize in designing fault-tolerant architectures and leading automation initiatives that improve reliability and developer velocity. Outside of work, I enjoy gardening, spending time in the gym, and creating memories with my family and friends.
          </p>
        </motion.section>

        {/* Experience Timeline */}
        <motion.section
          id="experience"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="w-full max-w-4xl mb-20"
        >
          <h2 className="text-3xl font-semibold text-center mb-8">Experience</h2>
          <div className="space-y-8">
            <TimelineItem
              role="Software Development Engineer II"
              company="Amazon Web Services (AWS)"
              years="Sep 2021 – Present"
              description={[
                "Engineered a Negative Profitability Filter at ASINFilterService’s control plane that flagged unprofitable ASINs at ingestion time, improving retail profitability by 2%.",
                "Modernized ASINFilterService filtering millions of ASINs/sec by migrating infrastructure to AWS CDK and JDK 17, improving deployment efficiency and maintainability.",
                "Built zero-touch region-build automation for AWS IAM, automating resource creation after region data merges — reducing setup time from weeks to hours.",
                "Implemented dynamic customer-limit management APIs for AWS MSK Serverless, removing manual config and improving runtime validation safety.",
                "Engineered availability-zone draining logic for AWS MSK Serverless that seamlessly reroutes traffic during outages, ensuring 99.99%+ service uptime.",
                "Designed and implemented a host remediation & monitoring system for AWS IAM’s 60K+ host data-plane fleet, improving availability by 15% and cutting manual ops by 99%.",
                "Led benchmarking suite for MSK clusters (Provisioned & Express Brokers) using custom Go/Python load generators, boosting throughput 10–15%.",
                "Re-engineered the alarm creation & deployment system (100K+ alarms globally) with per-service, wave-based rollouts and automated integration tests, reducing deployment time by 95%.",
                "Handled 30+ high-severity on-call pages/week for 1.7B TPS service, building automation that cut manual remediation by 90%."
              ]}
            />

            <TimelineItem
              role="Platform Engineer II"
              company="DreamWorks Animation"
              years="Jul 2019 – Sep 2021"
              description={[
                "Developed a Kubernetes backup operator supporting on-demand & scheduled backups for multiple DB types, improving recovery efficiency by 60%.",
                "Built and maintained CI/CD pipelines with Jenkins, Drone, and Spinnaker (Dinghy-as-code), standardizing 30+ service deployments and accelerating releases by 40%.",
                "Implemented microservice-based subscription system (Go, Kafka, PostgreSQL, Couchbase) for deployment notifications, improving visibility & traceability.",
                "Optimized Kubernetes/OpenShift routing via NGINX & HAProxy, achieving 25% cluster stability improvement.",
                "Automated new-service environment setups, reducing onboarding time from days to hours."
              ]}
            />

            <TimelineItem
              role="Web Developer I"
              company="University of Southern California (USC)"
              years="Jul 2017 – Jun 2019"
              description={[
                "Developed and launched 5+ full-stack production web apps for campus operations using Go, React.js, JS, HTML5, and CSS3 — enhancing workflows for multiple departments.",
                "Designed RESTful APIs and asynchronous pipelines, cutting load times by 40% and improving UX for 1K+ daily users.",
                "Integrated authentication, caching, and validation in Go/React, ensuring secure and efficient interactions.",
                "Enhanced site reliability by automating link & uptime checks, reducing manual maintenance by 30%.",
                "Built & maintained CI/CD pipelines with Jenkins and Puppet, reducing release turnaround by 25%.",
                "Collaborated with analytics & design teams on dashboards with real-time reporting, improving decision-making speed."
              ]}
            />

            <TimelineItem
              role="Software Engineer Intern"
              company="HoH Tech Labs"
              years="Aug 2016 – Jun 2017"
              description={[
                "Contributed to the design and development of web solutions using JavaScript, Node.js, and MongoDB.",
                "Optimized backend API response times and improved frontend UX with modular React components.",
                "Worked closely with the CTO to prototype features and integrate feedback into production cycles."
              ]}
            />
          </div>
        </motion.section>

        {/* Contact Section */}
        <motion.section
          id="contact"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl font-semibold mb-4">Get In Touch</h2>
          <p className="text-gray-700 dark:text-gray-400 mb-6">
            I’m open to full-time Software Engineering roles and collaborations — feel free to reach out!
          </p>

          <div className="flex justify-center gap-6 text-gray-600 dark:text-gray-400 text-xl">
            <a
              href="mailto:krishbhanushali@gmail.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex items-center gap-2"
            >
              <i className="ri-mail-line"></i> Email
            </a>
            <a
              href="https://www.linkedin.com/in/krishbhanushali"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex items-center gap-2"
            >
              <i className="ri-linkedin-box-line"></i> LinkedIn
            </a>
            <a
              href="https://github.com/krishbhanushali"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex items-center gap-2"
            >
              <i className="ri-github-line"></i> GitHub
            </a>

            <a
              href="https://medium.com/@krishbhanushali"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex items-center gap-2"
            >
              <i className="ri-medium-line"></i> Medium
            </a>
          </div>
        </motion.section>

      </div>
    </div>
  );
}

/* TimelineItem component */
/* Updated TimelineItem component */
function TimelineItem({ role, company, years, description }) {
  return (
    <div className="relative pl-6 border-l-2 border-blue-600 dark:border-blue-400">
      <div className="absolute w-3 h-3 bg-blue-600 dark:bg-blue-400 rounded-full -left-[7px] top-2"></div>

      <h3 className="text-xl font-semibold">{role}</h3>
      <p className="text-blue-600 dark:text-blue-400">{company}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{years}</p>

      <ul className="list-disc list-inside text-gray-700 dark:text-gray-400 space-y-1">
        {description.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

export default App;
